import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Autocomplete,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  styled,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { SAPIconButton, PageLayout, FilterBar } from '../components';
import { toast } from 'sonner';
import { logger } from '../utils/logger';
import { SAP_COLORS, SAP_SPACING, SAP_TYPOGRAPHY } from '../theme/sapDesignSystem';
import { fetchClients } from '../api/features/clientData/clientApi';
import { fetchActivityLogs, fetchActivityLogsCount } from '../api/features/activityLog/activityLogApi';
import { useClientCode } from '../api/auth/ClientCodeContext';
import type { Client } from '../api/features/clientData/client.types';
import type { ActivityLogData } from '../api/features/activityLog/activityLog.types';

const ROWS_PER_PAGE = 10;
const VALUE_CELL_MAX_HEIGHT = 100;

// Copyable value cell with copy button
function CopyableValueCell({
  value,
  emptyLabel = '—',
  color,
  fontWeight = 'regular',
}: {
  value: string | undefined;
  emptyLabel?: string;
  color: string;
  fontWeight?: 'regular' | 'medium';
}) {
  const trimmed = value?.trim() || '';
  const isDashesOnly = /^-+$/.test(trimmed.replace(/\s/g, ''));
  const isEmpty = !trimmed || isDashesOnly;
  const text = isEmpty ? emptyLabel : value!;
  const handleCopy = () => {
    if (isEmpty || text === emptyLabel) return;
    navigator.clipboard.writeText(value!).then(
      () => toast.success('Copied to clipboard'),
      () => toast.error('Failed to copy')
    );
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0 }}>
      <Typography
        variant="body2"
        component="div"
        sx={{
          flex: 1,
          minWidth: 0,
          maxHeight: VALUE_CELL_MAX_HEIGHT,
          overflowY: 'auto',
          whiteSpace: 'pre-line',
          color: isEmpty ? SAP_COLORS.sapGray4 : color,
          fontWeight: SAP_TYPOGRAPHY.fontWeight[fontWeight],
          fontSize: '0.8125rem',
          fontStyle: isEmpty ? 'italic' : 'normal',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: SAP_COLORS.sapGray3, borderRadius: 3 },
        }}
      >
        {isEmpty ? emptyLabel : value}
      </Typography>
      {!isEmpty && (
        <Tooltip title="Copy">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
            }}
            sx={{
              flexShrink: 0,
              p: 0.5,
              color: SAP_COLORS.sapGray5,
              '&:hover': { color: SAP_COLORS.sapBlue },
            }}
            aria-label="Copy"
          >
            <ContentCopy sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

const SAPTableHeader = styled(TableHead)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapLightBlue,
  '& .MuiTableCell-head': {
    fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
    color: SAP_COLORS.sapGray6,
    borderBottom: `2px solid ${SAP_COLORS.sapBlue}`,
  },
}));

export default function ActivityLog() {
  const { clientCode } = useClientCode();
  const isAdmin = clientCode === 'admin';
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [clientCodeFilter, setClientCodeFilter] = useState('');
  const [logs, setLogs] = useState<ActivityLogData[]>([]);
  const [count, setCount] = useState<number>(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Fetch clients for dropdown (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    const fetchClientOptions = async () => {
      try {
        const data = await fetchClients({ clientCode, firstResult: -1, maxResult: 0 });
        setClients(data);
      } catch (err) {
        logger.error('Failed to fetch clients:', err);
        setClients([]);
      }
    };

    fetchClientOptions();
  }, [clientCode, isAdmin]);

  // Fetch logs and count
  const fetchData = async (pageOverride?: number) => {
    setLoading(true);
    setError(null);
    try {
      const firstResult = (pageOverride ?? page) * ROWS_PER_PAGE;
      const params: any = {
        clientCode,
        firstResult,
        maxResult: ROWS_PER_PAGE,
      };
      if (selectedClient?.value) params.clientId = selectedClient.value;
      if (clientCodeFilter?.trim()) params.code = clientCodeFilter.trim();
      
      const [logData, logCount] = await Promise.all([
        fetchActivityLogs(params),
        fetchActivityLogsCount(params),
      ]);
      
      setLogs(logData);
      setCount(logCount);
    } catch (err: any) {
      logger.error('Activity Log Error:', err);
      setError('Failed to load activity logs');
      setLogs([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    setHasSearched(true);
    fetchData(0);
  };

  const handleReset = () => {
    setSelectedClient(null);
    setClientCodeFilter('');
    setPage(0);
    setLogs([]);
    setCount(0);
    setError(null);
    setHasSearched(false);
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
    fetchData(newPage);
  };

  const clientOptions = clients.map((client) => ({ label: client.name, value: client.id }));

  return (
    <PageLayout
      title="Activity Log"
      description="View what changes were made by users on Client Code"
      titleSx={{ color: SAP_COLORS.sapBlue, fontWeight: SAP_TYPOGRAPHY.fontWeight.bold }}
    >
      <FilterBar loading={loading}>
        {isAdmin && (
          <Box
            sx={{
              flex: { xs: '1 1 100%', md: '1 1 0' },
              minWidth: { xs: '100%', sm: 200 },
              maxWidth: { md: 'none' },
            }}
          >
            <Autocomplete
              options={clientOptions}
              value={selectedClient}
              onChange={(_, v) => setSelectedClient(v)}
              getOptionLabel={(option) => option?.label || ''}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              renderInput={(params) => (
                <TextField {...params} label="Client Name" fullWidth placeholder="Select client" />
              )}
              disabled={loading}
            />
          </Box>
        )}
        <Box
          sx={{
            flex: { xs: '1 1 100%', md: '1 1 0' },
            minWidth: { xs: '100%', sm: 200 },
            maxWidth: { md: 'none' },
          }}
        >
          <TextField
            label="Client Code"
            value={clientCodeFilter}
            onChange={(e) => setClientCodeFilter(e.target.value)}
            placeholder="Enter client code (partial match)"
            fullWidth
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            flexShrink: 0,
            alignSelf: { xs: 'stretch', md: 'center' },
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            width: { xs: '100%', md: 'auto' },
            ml: { md: 2 },
          }}
        >
          <SAPIconButton icon="search" variant="primary" onClick={handleSearch} disabled={loading} aria-label="Search" />
          <SAPIconButton icon="reset" variant="secondary" onClick={handleReset} disabled={loading} aria-label="Reset" />
        </Box>
      </FilterBar>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
          {error}
        </Alert>
      )}

      {/* Data Card */}
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          border: `1px solid ${SAP_COLORS.sapGray2}`,
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <CircularProgress sx={{ color: SAP_COLORS.sapBlue }} size={48} />
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <Alert severity="error" sx={{ 
                borderRadius: '8px',
                backgroundColor: SAP_COLORS.sapRed + '10',
                color: SAP_COLORS.sapRed,
                border: `1px solid ${SAP_COLORS.sapRed}30`,
              }}>
                {error}
              </Alert>
            </Box>
          ) : !hasSearched ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <Typography variant="body1" sx={{ color: SAP_COLORS.sapGray5 }}>
                Please use the search filters above to view activity logs.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
                <Table sx={{ minWidth: 700, tableLayout: 'auto' }} size="small">
                  {/* SAP Fiori Table Header */}
                  <SAPTableHeader>
                    <TableRow>
                      <TableCell
                        sx={{
                          color: SAP_COLORS.sapGray6,
                          fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                          width: { xs: 48, sm: 64 },
                          minWidth: 48,
                        }}
                      >
                        S.No.
                      </TableCell>
                      <TableCell
                        sx={{
                          color: SAP_COLORS.sapGray6,
                          fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                          minWidth: 100,
                        }}
                      >
                        Client Code
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold 
                      }}>
                        Description
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold 
                      }}>
                        Old Value
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold 
                      }}>
                        New Value
                      </TableCell>
                      <TableCell
                        sx={{
                          color: SAP_COLORS.sapGray6,
                          fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                          minWidth: 80,
                        }}
                      >
                        By whom
                      </TableCell>
                      <TableCell
                        sx={{
                          color: SAP_COLORS.sapGray6,
                          fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                          minWidth: 140,
                        }}
                      >
                        When
                      </TableCell>
                    </TableRow>
                  </SAPTableHeader>
                  
                  {/* Table Body */}
                  <TableBody>
                    {logs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ 
                          color: SAP_COLORS.sapGray5,
                          py: SAP_SPACING.xl,
                          fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                        }}>
                          No activity logs found. Try searching with different criteria.
                        </TableCell>
                      </TableRow>
                    ) : (
                      logs.map((log, idx) => (
                        <TableRow
                          key={log.id || idx}
                          sx={{
                            bgcolor: idx % 2 === 0 ? SAP_COLORS.sapWhite : SAP_COLORS.sapGray1,
                            '&:hover': { 
                              bgcolor: SAP_COLORS.hover,
                            },
                            transition: 'background-color 0.2s ease',
                          }}
                        >
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray6, 
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium 
                          }}>
                            {page * ROWS_PER_PAGE + idx + 1}
                          </TableCell>
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray7,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                          }}>
                            {log.code || 'N/A'}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: SAP_COLORS.sapGray7,
                              fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                              maxWidth: { xs: 140, sm: 180, md: 220 },
                              minWidth: 120,
                            }}
                          >
                            <Tooltip
                              title={log.descriptionSummary || log.fieldName || log.description || ''}
                              placement="top-start"
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  color: SAP_COLORS.sapGray7,
                                  maxWidth: '100%',
                                }}
                              >
                                {log.descriptionSummary ||
                                  log.fieldName ||
                                  (log.description && log.description.length > 60
                                    ? `${log.description.slice(0, 60)}…`
                                    : log.description) ||
                                  'N/A'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            sx={{
                              verticalAlign: 'top',
                              py: 1,
                              maxWidth: { xs: 160, sm: 200, md: 240 },
                              minWidth: 120,
                            }}
                          >
                            <CopyableValueCell
                              value={log.oldValue}
                              emptyLabel="(no changes)"
                              color={SAP_COLORS.sapGray5}
                              fontWeight="regular"
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              verticalAlign: 'top',
                              py: 1,
                              maxWidth: { xs: 160, sm: 200, md: 240 },
                              minWidth: 120,
                            }}
                          >
                            <CopyableValueCell
                              value={log.newValue}
                              emptyLabel="(no changes)"
                              color={SAP_COLORS.sapBlue}
                              fontWeight="medium"
                            />
                          </TableCell>
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray7,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                          }}>
                            <Typography
                              component="span"
                              title={log.modifiedUsernameFull || log.modifiedUsername || ''}
                            >
                              {log.modifiedUsername || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: SAP_COLORS.sapGray6 }}>
                            {log.formattedLastUpdated || 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination */}
              {logs.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    px: { xs: 1, sm: 2 },
                    pb: 2,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="body2" sx={{ color: SAP_COLORS.sapGray5, alignSelf: 'center' }}>
                    Showing {(page * ROWS_PER_PAGE) + 1}-{Math.min((page * ROWS_PER_PAGE) + logs.length, count)} of{' '}
                    {count} activities
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Rows per page</InputLabel>
                      <Select
                        value={ROWS_PER_PAGE}
                        onChange={(event: SelectChangeEvent<number>) => {
                          // Note: ROWS_PER_PAGE is constant, so this is just for UI consistency
                        }}
                        label="Rows per page"
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Pagination
                      count={Math.ceil(count / ROWS_PER_PAGE)}
                      page={page + 1}
                      onChange={(event: React.ChangeEvent<unknown>, newPage: number) => {
                        handleChangePage(event, newPage - 1);
                      }}
                      color="primary"
                      showFirstButton
                      showLastButton
                      size="small"
                      sx={{ '& .MuiPagination-ul': { flexWrap: 'wrap' } }}
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
} 